/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Children, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const ROTATION_INTERVAL_MS = 30_000;

interface AnnouncementCarouselProps {
    /**
     * One or more slide elements — typically `AnnouncementBanner` instances (see
     * `announcement-banner` spec, "Content source is pluggable, currently a local placeholder").
     * Each direct child is treated as one slide.
     */
    children: ReactNode;
    className?: string;
}

/**
 * Rotates through multiple announcement slides — 30-second auto-advance, slide transition, loop
 * after the last slide, single slide visible at a time, and (per the `announcement-banner` spec's
 * "Rotating messages cannot be dismissed" requirement) no dismiss control of its own. A single
 * slide renders directly with no timer and no transition (`site-header`/`announcement-banner`
 * spec, "Single message does not rotate").
 *
 * Content-source agnostic by design: this component only cares that it received slide elements as
 * children, not where they came from. `AnnouncementBannerPromo` currently feeds it slides from a
 * local placeholder (`get-promo-slides.server.ts`); a future Sanity-backed source can feed it the
 * same way once mapped to `AnnouncementBanner` elements, keeping rotation/timing logic shared
 * regardless of content source. Page Designer is deliberately not used for this content — see
 * that spec requirement.
 */
export default function AnnouncementCarousel({ children, className }: AnnouncementCarouselProps): ReactElement | null {
    const { t } = useTranslation('header');
    const slides = Children.toArray(children);
    const [activeIndex, setActiveIndex] = useState(0);

    const slideCount = slides.length;

    useEffect(() => {
        if (slideCount <= 1) {
            return;
        }
        const timer = setInterval(() => {
            setActiveIndex((current) => (current + 1) % slideCount);
        }, ROTATION_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [slideCount]);

    // Reset to the first slide if the slide count shrinks below the current index (e.g. an author
    // removes a message mid-session).
    useEffect(() => {
        if (activeIndex >= slideCount) {
            setActiveIndex(0);
        }
    }, [activeIndex, slideCount]);

    if (slideCount === 0) {
        return null;
    }

    if (slideCount === 1) {
        return <>{slides[0]}</>;
    }

    return (
        <div
            className={cn('relative overflow-hidden', className)}
            role="region"
            aria-roledescription="carousel"
            aria-label={t('announcementCarousel', 'Announcements')}>
            <div
                className="flex transition-transform duration-500 ease-in-out motion-reduce:transition-none"
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
                {slides.map((slide, index) => (
                    // eslint-disable-next-line react/no-array-index-key -- slides are positional, PD/Sanity content has no stable id here
                    <div key={index} className="w-full shrink-0" aria-hidden={index !== activeIndex}>
                        {slide}
                    </div>
                ))}
            </div>
        </div>
    );
}
